
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Image, Award } from "lucide-react";
import { Business } from "@/types";

interface AboutSectionProps {
  business: Business;
}

const AboutSection: React.FC<AboutSectionProps> = ({ business }) => {
  return (
    <Card>
      <CardContent className="p-6">
        <h2 className="text-2xl font-semibold mb-4">About</h2>
        <p className="text-muted-foreground mb-6">
          {business.description}
        </p>

        {business.tags && business.tags.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {business.tags.map((tag) => (
                <Badge key={tag} variant="outline">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {business.media_urls && business.media_urls.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2 flex items-center">
              <Image className="h-5 w-5 mr-2 text-eco-500" />
              Gallery
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {business.media_urls.map((url, index) => (
                <a 
                  key={index} 
                  href={url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block h-32 overflow-hidden rounded-md border border-gray-200 hover:opacity-90 transition-opacity"
                >
                  <img
                    src={url}
                    alt={`${business.name} image ${index + 1}`}
                    className="h-full w-full object-cover"
                  />
                </a>
              ))}
            </div>
          </div>
        )}

        {business.features && (
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2">Features</h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {business.features.map((feature) => (
                <li key={feature} className="flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2 text-eco-500" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {business.certifications && (
          <div>
            <h3 className="text-lg font-medium mb-2">
              Sustainability Certifications
            </h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {business.certifications.map((cert) => (
                <li key={cert} className="flex items-center">
                  <Award className="h-5 w-5 mr-2 text-eco-500" />
                  <span>{cert}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AboutSection;
